import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import WarehouseRoundedIcon from '@mui/icons-material/WarehouseRounded';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useInventorySettings, useUpdateInventorySettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type {
  InventoryTrackingMode,
  OutOfStockBehavior,
  ReservationPolicy,
} from '../../model/settingsTypes';
import {
  FeedbackSnackbar,
  NumberField,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TextInputField,
} from '../components';
import {
  INVENTORY_TRACKING_MODE_OPTIONS,
  OUT_OF_STOCK_BEHAVIOR_OPTIONS,
  RESERVATION_POLICY_OPTIONS,
} from '../options';

/** Inventory settings: stock tracking, reservation and availability policy. */
export function InventorySettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage } = useDomainPermissions('inventory');
  const query = useInventorySettings();
  const update = useUpdateInventorySettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success(t('inventorySettings.savedFeedback')),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title={t('inventorySettings.title')}
      subtitle={t('inventorySettings.subtitle')}
      icon={<WarehouseRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title={t('inventorySettings.trackingSection.title')} icon={<WarehouseRoundedIcon />}>
              <SelectField
                label={t('inventorySettings.trackingSection.trackingModeLabel')}
                value={draft.trackingMode}
                options={INVENTORY_TRACKING_MODE_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onChange={(value) => form.setField('trackingMode', value as InventoryTrackingMode)}
                disabled={disabled}
              />
              <TextInputField
                label={t('inventorySettings.trackingSection.defaultWarehouseCodeLabel')}
                value={draft.defaultWarehouseCode ?? ''}
                onChange={(value) => form.setField('defaultWarehouseCode', value)}
                disabled={disabled}
              />
              <NumberField
                label={t('inventorySettings.trackingSection.lowStockThresholdLabel')}
                description={t('inventorySettings.trackingSection.lowStockThresholdDescription')}
                value={draft.lowStockThreshold}
                onChange={(value) => form.setField('lowStockThreshold', value)}
                disabled={disabled}
                min={0}
                suffix={t('inventorySettings.trackingSection.lowStockThresholdSuffix')}
              />
            </SettingsCard>

            <SettingsCard title={t('inventorySettings.reservationSection.title')} icon={<TuneRoundedIcon />}>
              <SelectField
                label={t('inventorySettings.reservationSection.reservationPolicyLabel')}
                value={draft.reservationPolicy}
                options={RESERVATION_POLICY_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onChange={(value) => form.setField('reservationPolicy', value as ReservationPolicy)}
                disabled={disabled}
              />
              <NumberField
                label={t('inventorySettings.reservationSection.reservationTimeoutLabel')}
                description={t('inventorySettings.reservationSection.reservationTimeoutDescription')}
                value={draft.reservationTimeoutMinutes}
                onChange={(value) => form.setField('reservationTimeoutMinutes', value)}
                disabled={disabled}
                min={0}
                suffix={t('inventorySettings.reservationSection.reservationTimeoutSuffix')}
              />
            </SettingsCard>

            <SettingsCard title={t('inventorySettings.availabilitySection.title')} icon={<Inventory2RoundedIcon />}>
              <SwitchField
                label={t('inventorySettings.availabilitySection.allowBackorderLabel')}
                checked={draft.allowBackorder}
                onChange={(value) => form.setField('allowBackorder', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('inventorySettings.availabilitySection.allowPreorderLabel')}
                checked={draft.allowPreorder}
                onChange={(value) => form.setField('allowPreorder', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('inventorySettings.availabilitySection.allowOversellingLabel')}
                description={t('inventorySettings.availabilitySection.allowOversellingDescription')}
                checked={draft.oversellEnabled}
                onChange={(value) => form.setField('oversellEnabled', value)}
                disabled={disabled}
              />
              <SelectField
                label={t('inventorySettings.availabilitySection.outOfStockBehaviorLabel')}
                value={draft.outOfStockBehavior}
                options={OUT_OF_STOCK_BEHAVIOR_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onChange={(value) =>
                  form.setField('outOfStockBehavior', value as OutOfStockBehavior)
                }
                disabled={disabled}
              />
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
